import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  User,
  CheckCircle2,
  XCircle,
  IdCard,
  Shield,
  FileText,
  UserCog,
  Star,
  ArrowUpDown,
  MessageSquare,
  Trash2,
  AlertTriangle,
  Eye,
} from "lucide-react";
import { RatingInput, RatingDisplay } from "@/components/RatingStars";
import { getDocumentIcon, getDocumentLabel } from "./DocumentPreviewDialog";

export const WorkerDetailsDialog = ({
  open,
  onOpenChange,
  worker,
  onVerifyId,
  onVerifyDocument,
  onUpdateHmsStatus,
  onChangeRole,
  onSendMessage,
  onDelete,
  onDocumentPreview,
  onRefresh,
}) => {
  if (!worker) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-['Unbounded'] flex items-center justify-between">
            <span>Worker Details</span>
            <Button
              size="sm"
              variant="outline"
              onClick={() => onSendMessage(worker)}
              className="border-blue-500/50 text-blue-400 hover:bg-blue-500/10"
            >
              <MessageSquare className="w-4 h-4 mr-2" />
              Send Message
            </Button>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Header */}
          <div className="flex items-center gap-4">
            {worker.picture ? (
              <img
                src={worker.picture.startsWith("http") ? worker.picture : `${process.env.REACT_APP_BACKEND_URL}${worker.picture}`}
                alt={worker.name}
                className="w-16 h-16 rounded-full object-cover"
              />
            ) : (
              <div className="w-16 h-16 rounded-full bg-[#23F7DD]/20 flex items-center justify-center">
                <User className="w-8 h-8 text-[#23F7DD]" />
              </div>
            )}
            <div>
              <h3 className="text-xl font-bold text-white">{worker.name}</h3>
              <p className="text-[#94A3B8]">{worker.email}</p>
              {worker.profile?.worker_code && (
                <p className="text-[#23F7DD] font-mono">{worker.profile.worker_code}</p>
              )}
            </div>
          </div>

          {/* Norwegian ID Verification */}
          <div className="p-4 bg-white/5 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <IdCard className="w-5 h-5 text-[#23F7DD]" />
                <span className="text-white font-medium">Norwegian ID (Personnummer)</span>
              </div>
              {worker.profile?.norwegian_id_verified ? (
                <span className="px-2 py-1 bg-green-500/20 text-green-400 rounded text-xs">
                  Verified
                </span>
              ) : (
                <span className="px-2 py-1 bg-yellow-500/20 text-yellow-400 rounded text-xs">
                  Pending
                </span>
              )}
            </div>

            {worker.profile?.norwegian_id ? (
              <div className="flex items-center justify-between">
                <p className="font-mono text-lg text-white">{worker.profile.norwegian_id}</p>
                {!worker.profile.norwegian_id_verified && (
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={() => onVerifyId(worker.user_id, true)}
                      className="bg-green-500 text-white hover:bg-green-600"
                    >
                      <CheckCircle2 className="w-4 h-4 mr-1" />
                      Verify
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => onVerifyId(worker.user_id, false)}
                      className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                    >
                      <XCircle className="w-4 h-4 mr-1" />
                      Reject
                    </Button>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-[#94A3B8]">Not submitted</p>
            )}
          </div>

          {/* Documents */}
          <div className="p-4 bg-white/5 rounded-lg">
            <h4 className="text-white font-medium mb-4 flex items-center gap-2">
              <FileText className="w-5 h-5 text-[#23F7DD]" />
              Documents
            </h4>

            {worker.profile?.documents?.length > 0 ? (
              <div className="space-y-3">
                {worker.profile.documents.map((doc) => (
                  <div key={doc.document_id} className="flex items-center justify-between p-3 bg-black/30 rounded">
                    <div className="flex items-center gap-3">
                      {getDocumentIcon(doc.type)}
                      <div>
                        <p className="text-white">{getDocumentLabel(doc.type)}</p>
                        <p className="text-xs text-[#94A3B8]">{doc.name}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => onDocumentPreview(doc)}
                        className="text-[#23F7DD] hover:text-[#70FFEF] hover:bg-[#23F7DD]/10 h-7 px-2"
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        Preview
                      </Button>
                      {doc.verified ? (
                        <span className="px-2 py-1 bg-green-500/20 text-green-400 rounded text-xs">
                          Verified
                        </span>
                      ) : (
                        <div className="flex gap-1">
                          <Button
                            size="sm"
                            onClick={() => onVerifyDocument(worker.user_id, doc.document_id, true)}
                            className="bg-green-500 text-white hover:bg-green-600 h-7 px-2"
                          >
                            ✓
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => onVerifyDocument(worker.user_id, doc.document_id, false)}
                            className="border-red-500/50 text-red-400 hover:bg-red-500/10 h-7 px-2"
                          >
                            ✗
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-[#94A3B8]">No documents uploaded</p>
            )}
          </div>

          {/* HMS Card Status */}
          <div className="p-4 bg-white/5 rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-[#23F7DD]" />
                <span className="text-white font-medium">HMS Card Status</span>
              </div>
              <select
                value={worker.profile?.hms_card_status || "pending"}
                onChange={(e) => onUpdateHmsStatus(worker.user_id, e.target.value)}
                className="bg-black/50 border border-white/10 text-white px-3 py-1 rounded text-sm focus:border-[#23F7DD] focus:outline-none"
              >
                <option value="pending">Pending</option>
                <option value="verified">Verified</option>
                <option value="expired">Expired</option>
              </select>
            </div>
          </div>

          {/* Contract Status */}
          <div className="p-4 bg-white/5 rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-[#23F7DD]" />
                <span className="text-white font-medium">Employment Contract</span>
              </div>
              {worker.profile?.contract_signed ? (
                <div className="text-right">
                  <span className="px-2 py-1 bg-green-500/20 text-green-400 rounded text-xs">
                    Signed
                  </span>
                  <p className="text-xs text-[#94A3B8] mt-1">
                    {new Date(worker.profile.contract_signed_at).toLocaleString()}
                  </p>
                  <p className="text-xs text-[#94A3B8]">by: {worker.profile.contract_signature}</p>
                </div>
              ) : (
                <span className="px-2 py-1 bg-yellow-500/20 text-yellow-400 rounded text-xs">
                  Not Signed
                </span>
              )}
            </div>
          </div>

          {/* Role Management */}
          <div className="p-4 bg-purple-500/10 border border-purple-500/30 rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <UserCog className="w-5 h-5 text-purple-400" />
                <span className="text-white font-medium">Role Management</span>
              </div>
              <div className="flex items-center gap-3">
                <span
                  className={`px-3 py-1 rounded-full text-sm font-medium ${
                    worker.role === "team_leader"
                      ? "bg-purple-500/20 text-purple-400"
                      : "bg-[#23F7DD]/20 text-[#23F7DD]"
                  }`}
                >
                  {worker.role === "team_leader" ? "Team Leader" : "Worker"}
                </span>
                <Button
                  size="sm"
                  onClick={() =>
                    onChangeRole(worker.user_id, worker.role === "team_leader" ? "worker" : "team_leader")
                  }
                  className="bg-purple-500 text-white hover:bg-purple-600"
                >
                  <ArrowUpDown className="w-4 h-4 mr-1" />
                  {worker.role === "team_leader" ? "Demote to Worker" : "Promote to TL"}
                </Button>
              </div>
            </div>
            <p className="text-xs text-[#94A3B8] mt-2">
              Worker code will automatically change (WRK ↔ TL) when role is updated.
            </p>
          </div>

          {/* Additional Info */}
          {worker.profile && (
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-[#94A3B8]">Employment Type</p>
                <p className="text-white capitalize">{worker.profile.employment_type || "N/A"}</p>
              </div>
              <div>
                <p className="text-[#94A3B8]">Hourly Rate</p>
                <p className="text-white">{worker.profile.hourly_rate || 0} NOK</p>
              </div>
              <div>
                <p className="text-[#94A3B8]">Available for Work</p>
                <p className={worker.profile.is_available ? "text-green-400" : "text-[#94A3B8]"}>
                  {worker.profile.is_available ? "Yes" : "No"}
                </p>
              </div>
              <div>
                <p className="text-[#94A3B8]">Rating</p>
                <div className="flex items-center gap-2">
                  <RatingDisplay
                    rating={worker.profile.average_rating || 0}
                    totalRatings={worker.profile.total_ratings || 0}
                    size="sm"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Rate Worker */}
          <div className="p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Star className="w-5 h-5 text-yellow-400" />
                <span className="text-white font-medium">Rate This Worker</span>
              </div>
              <RatingInput
                workerId={worker.user_id}
                workerName={worker.name}
                onRatingAdded={onRefresh}
              />
            </div>
            <p className="text-xs text-[#94A3B8] mt-2">Give feedback on worker performance</p>
          </div>

          {/* Delete User - Danger Zone */}
          <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg mt-6">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="text-red-400 font-medium flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" />
                  Danger Zone
                </h4>
                <p className="text-xs text-[#94A3B8] mt-1">
                  Permanently delete this user and all their data
                </p>
              </div>
              <Button
                onClick={() => {
                  onOpenChange(false);
                  onDelete(worker);
                }}
                className="bg-red-500 text-white hover:bg-red-600"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete User
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
